import React, { useState, useEffect } from 'react'
import { Box, TextField, MenuItem, FormHelperText } from '@mui/material'

import GenericSelect from '../../../common/components/GenericSelect'
import { Field, useForm, useFormState } from 'react-final-form'
import { voiceTypesOptions } from './utils'
import AudioPlayerV2 from 'src/common/components/AudioPlayerV2'
import { usePhoneNumberOptions } from 'src/hooks'
import { parseJwt } from 'src/heplers'

const required = value => (value ? undefined : 'This field is required')

const deepfakeScriptOptions = [
  {
    name: 'HR Update Verification',
    value: 'Universal Scenario – "HR Update Verification"',
    description: `An employee receives a call from a voice claiming to be HR, stating there’s a problem with
their employment record or tax data.
They are asked to verbally confirm personal details including address, date of birth, and
bank account number.
The caller mentions this is part of a quarterly audit and needs immediate attention.`,
    disabled: false
  },
  {
    name: 'IT Helpdesk Callback',
    value: 'Universal Scenario – "IT Helpdesk Callback"',
    description: `A voice mimicking the internal IT helpdesk follows up on a supposed support ticket.
They request the user’s two-factor authentication code to verify identity and complete the
"remote troubleshooting session."
The tone is friendly and casual, lowering suspicion.`,
    disabled: false
  },
  {
    name: 'New Vendor Access Request',
    value: 'IT Team Scenario – "New Vendor Access Request"',
    description: `A voice claiming to be a new colleague from a partnered software company asks the IT
admin for temporary VPN access to finalize integration testing.
They mention the name of a real project and say a shared Google Sheet has already been
approved internally.
The caller insists it’s time-sensitive and approved by “your manager,” but doesn’t provide a
full name. `,
    disabled: false
  },
  {
    name: 'Supplier Bank Change',
    value: 'Finance Team Scenario – "Supplier Bank Change"',
    description: `A voice posing as a new contact from a regular supplier calls to inform the finance team of
an urgent change in bank details for upcoming payments.
They reference a real invoice number (easily scraped from email compromise) and provide
a new IBAN.
The caller is friendly but pushes for confirmation before end-of-day to avoid "late fees." `,
    disabled: false
  },
  {
    name: 'Freelancer Asset Access',
    value: 'Marketing Team Scenario – "Freelancer Asset Access"',
    description: `A voice claiming to be a newly hired freelance designer calls asking for access to the brand’s
internal design drive.
They say they’ve already signed an NDA and were told someone from marketing would
share the folder.
They use industry lingo to sound legitimate and reference past campaigns by name. `,
    disabled: false
  }
]

const langOptions = [
  {
    name: 'English',
    value: 'English',
    disabled: false
  },
  {
    name: 'Polish',
    value: 'Polish',
    disabled: false
  }
]
const langOptionsMedi = [
  {
    name: 'English',
    value: 'English',
    disabled: false
  },
  {
    name: 'English (South Africa)',
    value: 'English (South Africa)',
    disabled: false
  },
  {
    name: 'Switzerland',
    value: 'German',
    disabled: false
  }
]

const deepfakeAudioMap = {
  CEO_PHISHING: process.env.PUBLIC_URL + '/sounds/deepfake_example.ogg',
  HR_PHISHING: process.env.PUBLIC_URL + '/sounds/deepfake_example.ogg',
  COLLEAGUE_PHISHING: process.env.PUBLIC_URL + '/sounds/deepfake_example.ogg',
  Polish: {
    male: '/sounds/POL-MAN.m4a',
    female: '/sounds/POL-WOM.m4a'
  },
  English: {
    male: '/sounds/ENG-MAN.m4a',
    female: '/sounds/ENG-WOM.m4a'
  },
  'English (South Africa)': {
    male: '/sounds/ENG-AFR-MAN.m4a',
    female: '/sounds/ENG-AFR-WOM.m4a'
  },
  German: {
    male: '/sounds/DE-MAN.m4a',
    female: '/sounds/DE-WOM.m4a'
  }
}

export default function DeepfakeField({ hideTypeCard }) {
  const [selectedScript, setSelectedScript] = useState('')
  const phoneNumbersOptions = usePhoneNumberOptions(true)
  const { values } = useFormState()
  const form = useForm()

  const description = values?.metadata?.deepfakeScript
    ? deepfakeScriptOptions.find(el => el.value == values.metadata?.deepfakeScript)?.description
    : ''

  const userLocal = JSON.parse(localStorage.getItem('user'))
  const companyId = parseJwt(userLocal?.accessToken).companyId
  const mediIds = [
    '2ffd7fc7-6d0f-48b6-94c2-1aab115f1ae8', //local
    'a0f656e2-47ae-4022-85f6-208043644228',
    'c651ad24-8329-4181-9865-d3a99ffd6668',
    '67416a7e-e2af-464f-968a-390e7d17c1a8',
    'ab09d00b-c34d-4d1b-90c8-a5c830add241',
    '6cc805ed-85fe-4f3b-b2f2-f06b19d211e9'
  ]
  const isMediId = mediIds.includes(companyId)
  const filtredScenarioOptions = isMediId ? deepfakeScriptOptions.slice(0, 2) : deepfakeScriptOptions

  return (
    <Box display={'flex'} flexDirection={'column'} rowGap={2} pt={2}>
      {/* {!hideTypeCard && <ChooseTypeCard {...deepFakeCard} />} */}

      <div
        style={{
          width: '100%',
          height: 2,
          background: '#E7E8EA',
          marginBottom: 24
        }}
      ></div>

      <p
        style={{
          color: '#76767D'
        }}
      >
        Caller Settings
      </p>

      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          width: '100%'
        }}
      >
        <Field
          name={'metadata.phoneNumber'}
          validate={required}
          render={({ input, meta }) => (
            <>
              <GenericSelect
                width={'50%'}
                label={'Select phone number'}
                options={
                  isMediId ? phoneNumbersOptions.filter(el => el.phoneNumber !== '+12299999776') : phoneNumbersOptions
                }
                value={input.value}
                onChange={input.onChange}
                error={meta.error && meta.touched}
              />
              {meta.error && meta.touched && <FormHelperText error>{meta.error}</FormHelperText>}
            </>
          )}
        />
        <Field
          name={'metadata.voiceType'}
          validate={required}
          render={({ input, meta }) => (
            <>
              <GenericSelect
                width={'50%'}
                label={'Voice type'}
                options={voiceTypesOptions}
                value={input.value}
                onChange={e => {
                  if (isMediId) {
                    form.change('metadata', {
                      ...values.metadata,
                      deepfakeScript:
                        e.target.value === 'female'
                          ? 'Universal Scenario – "HR Update Verification"'
                          : 'Universal Scenario – "IT Helpdesk Callback"',
                      voiceType: e.target.value
                    })
                  } else {
                    input.onChange(e.target.value)
                  }
                }}
                error={meta.error && meta.touched}
              />
              {meta.error && meta.touched && <FormHelperText error>{meta.error}</FormHelperText>}
            </>
          )}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          width: '100%'
        }}
      >
        <Field
          name={'metadata.language'}
          validate={required}
          render={({ input, meta }) => {
            useEffect(() => {
              setSelectedScript(input.value)
            }, [input.value])

            return (
              <>
                <TextField
                  select
                  label='Language'
                  value={input.value}
                  onChange={e => {
                    input.onChange(e.target.value)
                    setSelectedScript(e.target.value)
                  }}
                  error={meta.error && meta.touched}
                  sx={{
                    width: '50%',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-root': {
                      fontSize: 16,
                      fontWeight: 400,
                      fontFamily: 'Plus Jakarta Sans',
                      borderRadius: '10px'
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '13px 14px 14px'
                    }
                  }}
                >
                  {isMediId
                    ? langOptionsMedi.map(option => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          sx={{
                            opacity: option.disabled ? 0.6 : 1
                          }}
                        >
                          {option.name}
                        </MenuItem>
                      ))
                    : langOptions.map(option => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          sx={{
                            opacity: option.disabled ? 0.6 : 1
                          }}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                </TextField>
                {meta.error && meta.touched && <FormHelperText error>{meta.error}</FormHelperText>}
              </>
            )
          }}
        />

        <Field
          name={'metadata.callerName'}
          validate={required}
          render={({ input, meta }) => {
            return (
              <>
                <TextField
                  label='Caller Name'
                  value={input.value}
                  onChange={e => {
                    const trimmedValue = e.target.value
                      .trim()
                      .replace(/[^\p{L}\s'-]/gu, '')
                      .replace(' ', '')
                      .slice(0, 15)
                    input.onChange(trimmedValue)
                  }}
                  error={meta.error && meta.touched}
                  sx={{
                    width: '50%',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-root': {
                      fontSize: 16,
                      fontWeight: 400,
                      fontFamily: 'Plus Jakarta Sans',
                      borderRadius: '10px'
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '13px 14px 14px'
                    }
                  }}
                />
                {meta.error && meta.touched && <FormHelperText error>{meta.error}</FormHelperText>}
              </>
            )
          }}
        />
      </Box>

      <p
        style={{
          color: '#76767D'
        }}
      >
        Scenario
      </p>

      <Field
        name={'metadata.deepfakeScript'}
        validate={required}
        render={({ input, meta }) => {
          useEffect(() => {
            setSelectedScript(input.value)
          }, [input.value])

          return (
            <>
              <TextField
                select
                label='Scenario type'
                value={input.value}
                onChange={e => {
                  setSelectedScript(e.target.value)

                  if (isMediId) {
                    form.change('metadata', {
                      ...values.metadata,
                      deepfakeScript: e.target.value,
                      voiceType: e.target.value == 'Universal Scenario – "HR Update Verification"' ? 'female' : 'male'
                    })
                  } else {
                    input.onChange(e.target.value)
                  }
                }}
                error={meta.error && meta.touched}
                sx={{
                  width: '100%',
                  backgroundColor: '#FFFFFF',
                  '& .MuiOutlinedInput-root': {
                    fontSize: 16,
                    fontWeight: 400,
                    fontFamily: 'Plus Jakarta Sans',
                    borderRadius: '10px'
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '13px 14px 14px'
                  }
                }}
              >
                {filtredScenarioOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    sx={{
                      opacity: option.disabled ? 0.6 : 1
                    }}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
              {meta?.error && meta?.touched && <FormHelperText error>{meta?.error}</FormHelperText>}
            </>
          )
        }}
      />

      {description && (
        <Box
          sx={{
            backgroundColor: '#0246F80D',
            borderRadius: '12px',
            padding: '12px 16px',
            display: 'flex',
            gap: '12px'
          }}
        >
          <svg
            style={{
              flexShrink: 0
            }}
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path opacity='0.2' d='M20.25 15H15V20.2507L20.25 15Z' fill='#0246F8' />
            <path d='M9 9H15' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
            <path d='M9 12H15' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
            <path d='M9 15H12' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
            <path
              d='M14.6893 20.25H4.5C4.30109 20.25 4.11032 20.171 3.96967 20.0303C3.82902 19.8897 3.75 19.6989 3.75 19.5V4.5C3.75 4.30109 3.82902 4.11032 3.96967 3.96967C4.11032 3.82902 4.30109 3.75 4.5 3.75H19.5C19.6989 3.75 19.8897 3.82902 20.0303 3.96967C20.171 4.11032 20.25 4.30109 20.25 4.5V14.6893C20.25 14.7878 20.2306 14.8854 20.1929 14.9764C20.1552 15.0673 20.1 15.15 20.0303 15.2197L15.2197 20.0303C15.15 20.1 15.0673 20.1552 14.9764 20.1929C14.8854 20.2306 14.7878 20.25 14.6893 20.25V20.25Z'
              stroke='#0246F8'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path d='M20.1825 15H15V20.1828' stroke='#0246F8' strokeLinecap='round' strokeLinejoin='round' />
          </svg>

          <p>{description ? description : ''}</p>
        </Box>
      )}

      {values.metadata?.language && values.metadata?.voiceType && (
        <Box
          sx={{
            width: '100%',
            marginTop: 2,
            marginBottom: 2
          }}
        >
          <AudioPlayerV2
            src={
              deepfakeAudioMap[isMediId ? values.metadata?.language : values.metadata?.language][
                values.metadata.voiceType
              ]
            }
          />
        </Box>
      )}
    </Box>
  )
}
